import React, { useEffect, useState } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import { Location } from 'history';
import { useTranslation } from '@fluentsms/i18n';

import NavigationAlertDialog, { NavigationAlertDialogContent } from '../NavigationAlertDialog';

export type NavigationPromptProps = {
  allowNavigation?: boolean;
  navigate?: (path: string) => void;
  id?: string;
  currentUrl?: string;
  alertMessage?: string;
  navPromptContent?: NavigationAlertDialogContent;
};

export const NavigationPrompt = ({
  allowNavigation,
  navigate,
  id,
  currentUrl,
  navPromptContent,
}: NavigationPromptProps): JSX.Element => {
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const [lastLocation, setLastLocation] = useState<Location | null>(null);

  const { t } = useTranslation('componentLibrary');

  const navContent: NavigationAlertDialogContent = {
    body: navPromptContent?.body || t('navigationAlertDialog.navContent.body'),
    options: {
      confirm: navPromptContent?.options?.confirm || t('navigationAlertDialog.navContent.options.confirm'),
      cancel: navPromptContent?.options?.cancel || t('navigationAlertDialog.navContent.options.cancel'),
    },
  };

  const history = useHistory();

  const navigateTo = navigate || history.push;
  const dialogLabelBy = `${id || 'navigation-prompt'}-alert`;
  const dialogDescribedBy = `${id || 'navigation-prompt'}-description`;

  const handleBlockedNavigation = (nextLocation: Location): boolean => {
    if (!confirmedNavigation && !allowNavigation && nextLocation.pathname !== currentUrl) {
      setOpenDialog(true);
      setLastLocation(nextLocation);
      return false;
    }
    return true;
  };

  const handleConfirm = () => {
    setOpenDialog(false);
    setConfirmedNavigation(true);
  };

  const handleCancel = () => {
    setOpenDialog(false);
  };

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      navigateTo(lastLocation.pathname);
    }
  }, [confirmedNavigation, lastLocation]);

  return (
    <>
      <Prompt when={true} message={handleBlockedNavigation} />
      <NavigationAlertDialog
        open={openDialog}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        labeledBy={dialogLabelBy}
        describedBy={dialogDescribedBy}
        navAlertContent={navContent}
      />
    </>
  );
};

export default NavigationPrompt;
